import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import {
  fetchJianyingTemplate,
  scanJianyingTemplate,
  updateJianyingTemplate,
  uploadJianyingTemplatePreview,
} from '../../services/jianyingTemplateService.js';
import { getJianyingTemplateStatusLabel } from '../../constants/jianyingTemplateStatus.js';
import JianyingCopyPresetEditor from './components/JianyingCopyPresetEditor.js';
import JianyingTemplatePreviewField from './components/JianyingTemplatePreviewField.js';
import JianyingTemplateTimelineEditor from './components/JianyingTemplateTimelineEditor.js';
import '../../../../jianying/JianyingCommon.scss';
import '../../JianyingTemplates.scss';

const TEMPLATE_LIST_PATH = '/system-management/jianying/templates';

const templateToForm = (template) => ({
  id: template.id,
  name: template.name || '',
  draft_name: template.draft_name || '',
  is_active: template.is_active !== false,
  template_preview: template.template_preview || null,
  video_type_ids: (template.video_type_ids || []).map(Number),
});

const getVideoTypeLabel = (videoType) => (
  videoType.title || videoType.videoType || videoType.name || `视频类型 ${videoType.id}`
);

const errorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

const SLOT_KIND_LABELS = {
  text: '文字',
  image: '图片',
  audio: '音频',
  video: '视频',
};

const STRUCTURE_FIELD_LABELS = {
  kind: '素材类型',
  material_id: '素材定位',
  segment_id: '片段定位',
  occurrence_id: '嵌套位置',
  target_start_us: '开始时间',
  target_duration_us: '片段时长',
  timeline_start_us: '主时间线位置',
  timeline_duration_us: '主时间线时长',
  source_start_us: '素材截取位置',
  source_duration_us: '素材截取时长',
  material_duration_us: '素材总时长',
};

const DifferenceSection = ({ title, items, tone }) => {
  if (!items?.length) return null;
  return (
    <section className={`jianying-structure-diff__section is-${tone}`}>
      <h6>{title}<span>{items.length}</span></h6>
      <ul>
        {items.map((item, index) => (
          <li key={`${item.id || item.name}-${index}`}>
            <span className="jianying-structure-diff__kind">
              {SLOT_KIND_LABELS[item.kind] || item.kind}
            </span>
            <strong>{item.name || '未命名槽位'}</strong>
            {item.changed_fields?.length ? (
              <small>
                {item.changed_fields
                  .map((field) => STRUCTURE_FIELD_LABELS[field] || field)
                  .join('、')}
              </small>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

const JianyingTemplateConfigurationPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [form, setForm] = useState(null);
  const [videoTypes, setVideoTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewRemoved, setPreviewRemoved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [structureDifference, setStructureDifference] = useState(null);

  const refreshEditorTemplate = useCallback(async () => {
    const nextTemplate = await fetchJianyingTemplate(templateId);
    setEditorTemplate((current) => (
      current?.id === templateId ? nextTemplate : current
    ));
    setForm((current) => (
      current.id === templateId ? templateToForm(nextTemplate) : current
    ));
    return nextTemplate;
  }, [templateId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage('');
    Promise.all([fetchJianyingTemplate(templateId), getVideoTypes()])
      .then(([template, types]) => {
        if (!active) return;
        setEditorTemplate(template);
        setForm(templateToForm(template));
        setVideoTypes(types || []);
      })
      .catch((error) => active && setMessage(errorMessage(error, '读取模板配置失败')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [templateId]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleVideoType = (videoTypeId) => {
    const id = Number(videoTypeId);
    setForm((current) => ({
      ...current,
      video_type_ids: current.video_type_ids.includes(id)
        ? current.video_type_ids.filter((value) => value !== id)
        : [...current.video_type_ids, id],
    }));
  };

  const saveBasicInformation = async () => {
    if (!form?.name.trim()) {
      setMessage('模板名称不能为空');
      return;
    }
    if (!form.video_type_ids.length) {
      setMessage('请至少选择一个适用视频类型');
      return;
    }
    setBusy(true);
    setMessage('');
    let previewUpload = null;
    let previewUploadError = null;
    try {
      if (previewFile) {
        try {
          previewUpload = await uploadJianyingTemplatePreview(previewFile);
        } catch (error) {
          previewUploadError = error;
        }
      }
      const previewPatch = previewUpload?.object_key
        ? { template_preview_object_key: previewUpload.object_key }
        : previewRemoved
          ? { remove_template_preview: true }
          : {};
      const template = await updateJianyingTemplate(templateId, {
        name: form.name.trim(),
        is_active: form.is_active,
        video_type_ids: form.video_type_ids,
        ...previewPatch,
      });
      setEditorTemplate(template);
      setForm(templateToForm(template));
      setPreviewFile(null);
      setPreviewRemoved(false);
      setMessage(previewUploadError
        ? '模板已保存，但预览视频上传失败；可以稍后重试。'
        : '模板信息已保存');
    } catch (error) {
      setMessage(errorMessage(error, '保存模板信息失败'));
    } finally {
      setBusy(false);
    }
  };

  const rescanTemplate = async () => {
    setBusy(true);
    setMessage('');
    try {
      const result = await scanJianyingTemplate(templateId);
      if (result.structure_changed) {
        setStructureDifference(result);
        return;
      }
      await refreshEditorTemplate();
      setMessage('模板结构校验通过');
    } catch (error) {
      setMessage(errorMessage(error, '模板结构校验失败'));
    } finally {
      setBusy(false);
    }
  };

  const confirmStructureSync = async () => {
    if (!structureDifference?.current_structure_hash) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await scanJianyingTemplate(templateId, {
        accept_structure_hash: structureDifference.current_structure_hash,
      });
      if (result.structure_changed) {
        setStructureDifference(result);
        setMessage('模板在确认前再次发生变化，请重新检查差异');
        return;
      }
      setStructureDifference(null);
      await refreshEditorTemplate();
      setMessage('模板结构已同步');
    } catch (error) {
      setMessage(errorMessage(error, '同步模板结构失败'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="jianying-template-config-state"><Spinner size="sm" /> 正在读取模板配置</div>;
  }

  if (!editorTemplate || !form) {
    return (
      <div className="jianying-template-config-state">
        <p>{message || '模板不存在'}</p>
        <button
          type="button"
          className="jianying-template-editor-back"
          aria-label="返回"
          title="返回"
          onClick={() => navigate(TEMPLATE_LIST_PATH)}
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="jianying-template-editor-page jianying-workspace">
      <header className="jianying-template-editor-head">
        <div className="jianying-template-editor-head__identity">
          <div className="jianying-template-editor-head__route">
            <button
              type="button"
              className="jianying-template-editor-back"
              aria-label="返回"
              title="返回"
              onClick={() => navigate(TEMPLATE_LIST_PATH)}
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
            </button>
            <div className="jianying-template-editor-crumb">系统管理 / 剪映模板 / {editorTemplate.name}</div>
          </div>
        </div>
        <div className="jianying-template-editor-head__actions">
          <span className="jianying-template-editor-status">
            {getJianyingTemplateStatusLabel(editorTemplate.status)}
          </span>
          <Button
            className="jianying-template-editor-validate"
            size="sm"
            variant="outline-secondary"
            disabled={busy}
            onClick={rescanTemplate}
          >
            <i className="bi bi-arrow-repeat" /> 校验模板结构
          </Button>
          {activeTab === 'basic' ? (
            <Button disabled={busy} onClick={saveBasicInformation}>
              {busy ? <Spinner size="sm" /> : '保存模板信息'}
            </Button>
          ) : null}
        </div>
      </header>

      <nav className="jianying-template-editor-tabs" aria-label="模板配置内容">
        <button type="button" className={activeTab === 'timeline' ? 'is-active' : ''} onClick={() => setActiveTab('timeline')}>
          槽位与分组 <span>{editorTemplate.slots?.length || 0}</span>
        </button>
        <button type="button" className={activeTab === 'presets' ? 'is-active' : ''} onClick={() => setActiveTab('presets')}>
          文案模板 <span>{editorTemplate.copy_presets?.length || 0}</span>
        </button>
        <button type="button" className={activeTab === 'basic' ? 'is-active' : ''} onClick={() => setActiveTab('basic')}>
          模板信息
        </button>
      </nav>

      <main className={`jianying-template-editor-content is-${activeTab}`}>
        {message ? <p className="jianying-notice" role="alert">{message}</p> : null}
        {activeTab === 'timeline' ? (
          <JianyingTemplateTimelineEditor template={editorTemplate} onChanged={refreshEditorTemplate} />
        ) : null}
        {activeTab === 'presets' ? (
          <JianyingCopyPresetEditor template={editorTemplate} onChanged={refreshEditorTemplate} />
        ) : null}
        {activeTab === 'basic' ? (
          <section className="jianying-template-basic jianying-template-basic--page">
            <div>
              <h2>模板信息</h2>
              <p>草稿定位由首次扫描确定，保存时只更新业务信息。</p>
            </div>
            <div className="jianying-form-grid">
              <Form.Group controlId="jianying-template-name">
                <Form.Label>模板名称</Form.Label>
                <Form.Control value={form.name} onChange={(event) => setField('name', event.target.value)} disabled={busy} />
              </Form.Group>
              <Form.Group controlId="jianying-template-draft-name">
                <Form.Label>剪映草稿名</Form.Label>
                <Form.Control value={form.draft_name} disabled />
                <Form.Text>草稿名创建后不可修改。</Form.Text>
              </Form.Group>
              <Form.Group className="jianying-form-grid__wide">
                <Form.Label>适用视频类型</Form.Label>
                <div className="jianying-check-grid">
                  {videoTypes.map((videoType) => (
                    <Form.Check key={videoType.id} id={`jianying-video-type-${videoType.id}`} type="checkbox" label={getVideoTypeLabel(videoType)} checked={form.video_type_ids.includes(Number(videoType.id))} onChange={() => toggleVideoType(videoType.id)} disabled={busy} />
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="jianying-form-grid__wide">
                <Form.Check type="switch" id="jianying-template-active" label="启用模板" checked={form.is_active} onChange={(event) => setField('is_active', event.target.checked)} disabled={busy} />
              </Form.Group>
            </div>
            <JianyingTemplatePreviewField
              disabled={busy}
              onFileChange={(file) => { setPreviewFile(file); setPreviewRemoved(false); }}
              onRemove={() => { setPreviewFile(null); setPreviewRemoved(true); setField('template_preview', null); }}
              pendingFile={previewFile}
              previewUrl={form.template_preview}
              templateName={form.name}
            />
          </section>
        ) : null}
      </main>
      <Modal
        show={Boolean(structureDifference)}
        onHide={() => !busy && setStructureDifference(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>模板结构发生变化</Modal.Title>
        </Modal.Header>
        <Modal.Body className="jianying-structure-diff">
          <p>
            当前剪映草稿与已保存配置不一致。确认同步后，未变化槽位的名称、分组、
            必填设置和文案内容都会保留。
          </p>
          <DifferenceSection
            title="新增槽位"
            tone="added"
            items={structureDifference?.difference?.added}
          />
          <DifferenceSection
            title="删除槽位"
            tone="removed"
            items={structureDifference?.difference?.removed}
          />
          <DifferenceSection
            title="变化槽位"
            tone="changed"
            items={structureDifference?.difference?.changed}
          />
          {structureDifference?.difference?.removed?.length ? (
            <p className="jianying-structure-diff__warning">
              删除槽位同步后会从模板配置和文案模板中移除。
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={busy}
            onClick={() => setStructureDifference(null)}
          >
            暂不同步
          </Button>
          <Button disabled={busy} onClick={confirmStructureSync}>
            {busy ? <Spinner size="sm" /> : '同步当前结构'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JianyingTemplateConfigurationPage;
